"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

type Heading = {
  level: number;
  text: string;
  id: string;
};

type Props = {
  headings: Heading[];
};

export default function TableOfContents({ headings }: Props) {
  const [activeId, setActiveId] = useState<string>(headings[0]?.id ?? "");
  const headingElementsRef = useRef<Record<string, IntersectionObserverEntry>>({});

  useEffect(() => {
    if (headings.length === 0) return;

    const callback = (entries: IntersectionObserverEntry[]) => {
      // Accumule toutes les entrées observées
      entries.forEach((entry) => {
        headingElementsRef.current[entry.target.id] = entry;
      });

      // Parmi tous les headings observés, trouve celui le plus proche du haut
      // en tenant compte de leur ordre dans le document
      const visibleHeadings = headings.filter(
        ({ id }) => headingElementsRef.current[id]?.isIntersecting
      );

      if (visibleHeadings.length > 0) {
        // Prend le premier visible dans l'ordre du document
        setActiveId(visibleHeadings[0].id);
        return;
      }

      // Aucun heading visible : on cherche le dernier heading
      // qui est passé au-dessus du viewport
      const passedHeadings = headings.filter(({ id }) => {
        const entry = headingElementsRef.current[id];
        if (!entry) return false;
        return entry.boundingClientRect.top < 0;
      });

      if (passedHeadings.length > 0) {
        setActiveId(passedHeadings[passedHeadings.length - 1].id);
      }
    };

    const observer = new IntersectionObserver(callback, {
      root: null,
      // Légère marge haute pour déclencher un peu avant d'atteindre le heading
      rootMargin: "-80px 0px -40% 0px",
      threshold: 0,
    });

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    // Gestion du hash initial dans l'URL
    const hash = window.location.hash.replace("#", "");
    if (hash && headings.some((h) => h.id === hash)) {
      setActiveId(hash);
    }

    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <aside className="w-full max-w-[260px]  border border-white/10 rounded-2xl p-4 bg-surface-container-low">
      <p className="text-xs uppercase tracking-widest font-mono text-on-surface-variant mb-3">
        Sommaire
      </p>
      <ul className="space-y-2">
        {headings.map((heading) => {
          const isActive = heading.id === activeId;
          return (
            <li key={heading.id}>
              <Link
                href={`#${heading.id}`}
                onClick={() => setActiveId(heading.id)}
                className={`block text-sm wrap-break-word transition-colors ${heading.level === 3 ? "pl-4" : ""
                  } ${isActive ? "text-white font-semibold" : "text-white/70 hover:text-white"
                  }`}
              >
                {heading.text}
              </Link>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}